import { EditorView, keymap } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { useRef, useState } from 'react';

import { useOnClickOutside } from '@/hooks/useOutsideClick';
import { variableHighlightPlugin } from '@/utils/Json/VariableHighlightPlugin';

import HintBlock from '../../../../common/HintBlock';
import { ActionsMenu } from '../../actions/ActionsMenu';

const customLightTheme = EditorView.theme(
  {
    '&': {
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      color: '#000000',
      height: '100%',
      position: 'relative',
      overflow: 'auto',
      outline: 'none',
      fontFamily: 'Lexend',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    '.cm-activeLine': {
      backgroundColor: '#ffffff',
      border: 'none',
      fontFamily: 'Lexend',
      fontWeight: 400,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    '.cm-line': {
      fontFamily: 'var(--bs-body-font-family)',
      fontWeight: 400,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    '.cm-focused': {
      outline: 'none',
    },
    '.cm-editor': {
      outline: 'none',
      overflowX: 'auto', // Enable horizontal scroll
      whiteSpace: 'pre',
    },
    '.cm-content': {
      caretColor: '#000000',
      lineHeight: '1.5',
      overflowX: 'auto', // Ensure content allows horizontal scrolling
      whiteSpace: 'pre', // Prevent word wrapping
      minWidth: '100%',
    },
    '.cm-tooltip': {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #d8000c',
      margin: '5px',
      zIndex: '1000',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    '.cm-tooltip-lint': {
      backgroundColor: '#ffe6e6',
      border: '1px solid #d8000c',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '10px',
      position: 'absolute',
      top: '100%',
      left: '0',
      marginTop: '5px',
      marginBottom: '50px',
      width: '300px',
      wordWrap: 'break-word',
      lineHeight: '1.5',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    '.cm-lint-marker-error': {
      color: '#d8000c',
      fontSize: '1.2em',
      cursor: 'pointer',
    },
    '.cm-tooltip-error': {
      background: '#d8000c',
      color: '#ffffff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.875em',
    },
    '.variable-highlight': {
      alignItems: 'center',
      justifyContent: 'center',
      height: 'max-content',
      padding: '2px 5px',
      backgroundColor: '#d1fae5',
      borderRadius: '0.75rem',
      maxWidth: '200px',
    },
    '.removed-variable-highlight': {
      alignItems: 'center',
      justifyContent: 'center',
      height: 'max-content',
      padding: '2px 5px',
      backgroundColor: '#f8a665',
      borderRadius: '0.75rem',
      maxWidth: '200px',
    },
  },
  { dark: false }
);

interface ScenarioJSONInputProps {
  contextMenuList: any[];
  value: string;
  setValue: (text: string) => void;
  placeholder: string;
  className?: string;
  isInvalid?: boolean;
  isMultiline?: boolean;
  showCallupFunctionsButton?: boolean;
  disabled?: boolean;
}

export const ScenarioSimpleTextInput = ({ showCallupFunctionsButton = true, ...rest }: ScenarioJSONInputProps) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const moduleRef = useRef<HTMLDivElement | null>(null);
  const editorViewRef = useRef<EditorView | null>(null); // Référence pour accéder à l'éditeur

  useOnClickOutside(moduleRef, () => {
    setShowContextMenu(false);
  });

  const keymaps = keymap.of([]);

  const handleChange = (value: string) => {
    rest.setValue(value);
    setShowContextMenu(false);
  };

  const onSelect = (e: any) => {
    addText(e?.variableName);
    setShowContextMenu(false);
  };

  const addText = (text: string) => {
    if (editorViewRef.current) {
      editorViewRef.current.focus();
      const { state, dispatch } = editorViewRef.current;
      const { from, to } = state.selection.main;
      dispatch(
        state.update({
          changes: { from, to, insert: text },
        })
      );
    }
  };

  return (
    <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-z-1">
      <div className="tw-relative tw-flex">
        <div
          className={`tw-overflow-visible tw-relative tw-flex-grow tw-overflow-x-auto tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 ${
            rest.isInvalid ? 'is-invalid' : ''
          } ${rest.className ? rest.className : ''}`}
        >
          <CodeMirror
            value={rest.value}
            height={rest.isMultiline ? '200px' : 'auto'}
            extensions={[customLightTheme, keymaps, variableHighlightPlugin]}
            onChange={handleChange}
            basicSetup={{
              lintKeymap: true,
              foldGutter: false,
              highlightActiveLine: false,
              lineNumbers: false,
            }}
            placeholder={rest.placeholder}
            onCreateEditor={view => {
              editorViewRef.current = view;
            }}
            editable={!rest.disabled}
          />
        </div>
      </div>
      {showContextMenu && (
        <div
          className="tw-absolute tw-bg-white tw-shadow-lg tw-border tw-border-gray-300"
          style={{
            top: '10px',
            left: '10px',
          }}
          ref={moduleRef}
        >
          <ActionsMenu contextMenuList={rest.contextMenuList} onSelect={onSelect} />
        </div>
      )}
      {showCallupFunctionsButton && (
        <button
          id="btn-hint-callup"
          className="tw-border-none tw-px-3 tw-py-2 tw-self-stretch tw-bg-gray-100 tw-rounded-bl-2xl tw-rounded-br-2xl tw-inline-flex"
          onClick={() => setShowContextMenu(true)}
          type="button"
        >
          <HintBlock texts={['ScenarioTextInput/key1']} />
        </button>
      )}
    </div>
  );
};
